import { useEffect } from "react";

const SITE_NAME = "Data Apparatus";
const SITE_URL = "https://dataapparatus.com.au";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      element.removeAttribute(key);
      return;
    }
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function upsertJsonLd(id, data) {
  let element = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.dataset.seoId = id;
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function serviceSchema(title, description, url) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title.replace(" | Data Apparatus", ""),
    description,
    provider: {
      "@type": "ProfessionalService",
      name: SITE_NAME,
      url: SITE_URL,
      areaServed: ["Australia", "Remote"],
    },
    serviceType: "Data engineering, data architecture and analytics consulting",
    url,
  };
}

function breadcrumbSchema(path, title, canonicalUrl) {
  const crumbs = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
  ];

  const segments = path.split("/").filter(Boolean);
  let current = SITE_URL;

  segments.forEach((segment, index) => {
    current += `/${segment}`;
    crumbs.push({
      "@type": "ListItem",
      position: index + 2,
      name:
        index === segments.length - 1
          ? title.replace(" | Data Apparatus", "")
          : segment
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
      item: index === segments.length - 1 ? canonicalUrl : current,
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs,
  };
}

export default function Seo({
  title,
  description,
  canonicalPath,
  image = DEFAULT_IMAGE,
  type = "website",
  schema,
}) {
  useEffect(() => {
    const path = canonicalPath || window.location.pathname || "/";
    const normalizedPath = path === "/" ? "/" : path.replace(/\/$/, "");
    const canonicalUrl = `${SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;

    document.title = title;

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index, follow" });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    const organization = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_IMAGE,
      image,
      description:
        "Data Apparatus provides data architecture, data engineering, integration, automation and analytics consulting for operational, financial and sustainability reporting.",
      areaServed: ["Australia", "Remote"],
      founder: {
        "@type": "Person",
        name: "Nurlan Alizada",
      },
      sameAs: ["https://www.linkedin.com/company/114324074/"],
    };

    upsertJsonLd("organization", organization);
    upsertJsonLd("breadcrumb", breadcrumbSchema(normalizedPath, title, canonicalUrl));
    upsertJsonLd("page-service", schema || serviceSchema(title, description, canonicalUrl));
  }, [canonicalPath, description, image, schema, title, type]);

  return null;
}
